import type {
  AvailabilityStatusEnum,
  MealPeriodsInput,
  ProductEnum,
  SubmissionStatusEnum,
  SubmitVendorAvailabilityInput,
  WindowStatusEnum,
} from '@/api';

// Availability feature only handles POPUP and DELIVERY — CATERING is out of scope
// (server rejects CATERING submissions via SubmitVendorAvailability validation).
export type ProductType = Extract<ProductEnum, ProductEnum.POPUP | ProductEnum.DELIVERY>;

export type AvailabilityStatus = AvailabilityStatusEnum;
export type SubmissionStatus = SubmissionStatusEnum;
export type WindowStatus = WindowStatusEnum;

// MealPeriodsInput matches the schema's AvailabilityMealPeriods shape exactly.
export type MealPeriods = MealPeriodsInput;
export type EligibleMealPeriods = MealPeriodsInput;

// UI-only state machine — schema has separate submission/window statuses,
// EDITING and PAST_CUTOFF are frontend-derived states.
export enum SubmissionState {
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  SUBMITTED = 'SUBMITTED',
  EDITING = 'EDITING',
  PAST_CUTOFF = 'PAST_CUTOFF',
}

// Quick-action presets for the availability calendar.
export enum QuickAction {
  MON_FRI = 'mon-fri',
  EVERYDAY = 'everyday',
  UNAVAILABLE = 'unavailable',
}

// Meal period keys for UI labels/iteration.
// Values must match MealPeriodsInput field names so they can index it directly.
export enum MealPeriod {
  BREAKFAST = 'breakfast',
  LUNCH = 'lunch',
  DINNER = 'dinner',
}

// Clean domain shape consumed by components (strict non-null after hook mapping).
export type VendorAvailabilityListItem = {
  vendorId: string;
  internalName: string;
  markets: string[];
  productType: ProductType;
  availabilityStatus: AvailabilityStatus;
  submissionStatus: SubmissionStatus;
  windowStatus: WindowStatus;
  deadlineDate: string | null;
  requiresSubmission: boolean;
  submission: Submission | null;
};

export type AvailabilityDate = {
  date: string;
  available: boolean;
};

export type Submission = {
  submittedAt: string;
  submittedByEmail: string;
  version: number;
};

export type VendorAvailabilityData = {
  dates: AvailabilityDate[];
  mealPeriods: MealPeriods | null;
  eligibleMealPeriods: EligibleMealPeriods | null;
  submission: Submission | null;
  submissionStatus: SubmissionStatus;
  windowStatus: WindowStatus;
  deadlineDate: string | null;
  requiresSubmission: boolean;
};

// Direct reuse of generated mutation input type.
export type SubmitAvailabilityInput = SubmitVendorAvailabilityInput;

export type SubmitAvailabilityResult = {
  success: boolean;
  errors: string[];
};

// Frontend-only composite key for list selection (no schema equivalent).
export type RestaurantKey = {
  vendorId: string;
  productType: ProductType;
};

export type BadgeVariant = {
  backgroundColor: string;
  color: string;
  getText: (data: VendorAvailabilityData) => string;
  extraText?: boolean;
};

export type BadgeVariantKey =
  | 'ACTION_REQUIRED'
  | 'SUBMITTED'
  | 'NO_SUBMISSION_REQUIRED'
  | 'UPDATED'
  | 'NOT_SUBMITTED_LOCKED';
